<template>
  <div class="space-y-6">
    <!-- 헤더 -->
    <div class="glass-card p-6">
      <div class="flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h2 class="text-2xl font-bold text-gradient mb-2">
            당첨번호 히스토리
          </h2>
          <p class="text-gray-600">과거 당첨번호 기록과 상세 분석</p>
        </div>
        <div class="mt-4 md:mt-0 flex items-center space-x-4">
          <select
            v-model="selectedFilter"
            class="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">전체 보기</option>
            <option value="recent">최근 20회</option>
            <option value="high-sum">고합계</option>
            <option value="consecutive">연속번호</option>
          </select>
          <button
            @click="refreshHistory"
            class="btn-secondary inline-flex items-center space-x-2"
          >
            <span>새로고침</span>
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              ></path>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- 요약 통계 -->
    <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
      <div class="glass-card p-4 text-center">
        <div class="text-2xl font-bold text-blue-600">{{ totalDraws }}</div>
        <div class="text-sm text-gray-600">총 회차</div>
      </div>
      <div class="glass-card p-4 text-center">
        <div class="text-2xl font-bold text-green-600">{{ avgSum }}</div>
        <div class="text-sm text-gray-600">평균 합계</div>
      </div>
      <div class="glass-card p-4 text-center">
        <div class="text-2xl font-bold text-purple-600">
          {{ consecutiveRate }}
        </div>
        <div class="text-sm text-gray-600">연속번호율</div>
      </div>
      <div class="glass-card p-4 text-center">
        <div class="text-2xl font-bold text-orange-600">{{ oddRate }}</div>
        <div class="text-sm text-gray-600">홀수 비율</div>
      </div>
    </div>

    <!-- 히스토리 테이블 -->
    <div class="glass-card overflow-hidden">
      <div class="px-6 py-4 border-b border-gray-200">
        <h3 class="text-lg font-semibold">당첨번호 기록</h3>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-gray-50">
            <tr>
              <th
                class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                회차
              </th>
              <th
                class="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                당첨번호 + 보너스
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr
              v-for="(draw, index) in filteredHistory"
              :key="draw.round"
              :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
              class="hover:bg-blue-50 transition-colors duration-200"
            >
              <td class="px-6 py-6 whitespace-nowrap">
                <div class="text-base font-bold text-gray-900">
                  {{ draw.round }}회
                </div>
                <div class="text-xs text-gray-500 mt-1">
                  {{ formatDate(draw.date) }}
                </div>
              </td>
              <td class="px-6 py-6">
                <div class="flex items-center justify-center space-x-3">
                  <span
                    v-for="number in draw.numbers"
                    :key="number"
                    class="flex items-center justify-center w-12 h-12 rounded-full font-bold text-white text-base shadow-lg transform hover:scale-110 transition-transform"
                    :class="getBallClass(number)"
                  >
                    {{ number }}
                  </span>
                  <div class="mx-2 text-gray-400 text-xl">+</div>
                  <span class="flex items-center justify-center w-12 h-12 rounded-full font-bold text-white text-base shadow-lg bg-gradient-to-r from-orange-400 to-orange-600 border-2 border-yellow-400">
                    {{ draw.bonus }}
                  </span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- 페이지네이션 -->
      <div class="px-6 py-4 border-t border-gray-200">
        <div class="flex items-center justify-between">
          <div class="text-sm text-gray-700">
            총 {{ totalDraws }}회차 중 {{ filteredHistory.length }}회차 표시
          </div>
          <div class="flex items-center space-x-2">
            <button
              @click="currentPage > 1 && currentPage--"
              :disabled="currentPage <= 1"
              class="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              이전
            </button>
            <span class="px-3 py-1 text-sm">
              {{ currentPage }} / {{ totalPages }}
            </span>
            <button
              @click="currentPage < totalPages && currentPage++"
              :disabled="currentPage >= totalPages"
              class="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              다음
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from "vue";
import { loadLottoData } from "../utils/lottoUtils";

export default {
  name: "History",
  setup() {
    const historyData = ref([]);
    const selectedFilter = ref("all");
    const currentPage = ref(1);
    const itemsPerPage = ref(20);

    const totalDraws = computed(() => historyData.value.length);
    const avgSum = computed(() => {
      if (historyData.value.length === 0) return 0;
      const total = historyData.value.reduce((sum, draw) => sum + draw.sum, 0);
      return Math.round(total / historyData.value.length);
    });
    const consecutiveRate = computed(() => {
      const consecutiveCount = historyData.value.filter((draw) =>
        draw.features.includes("연속")
      ).length;
      return Math.round((consecutiveCount / totalDraws.value) * 100) + "%";
    });
    const oddRate = computed(() => {
      const oddCount = historyData.value.reduce((count, draw) => {
        const odds = draw.numbers.filter((num) => num % 2 === 1).length;
        return count + odds;
      }, 0);
      const totalNumbers = totalDraws.value * 6;
      return Math.round((oddCount / totalNumbers) * 100) + "%";
    });

    const filteredHistory = computed(() => {
      let filtered = [...historyData.value];

      switch (selectedFilter.value) {
        case "recent":
          filtered = filtered.slice(-20);
          break;
        case "high-sum":
          filtered = filtered.filter((draw) => draw.sum > 170);
          break;
        case "consecutive":
          filtered = filtered.filter((draw) => draw.features.includes("연속"));
          break;
      }

      return filtered
        .reverse()
        .slice(
          (currentPage.value - 1) * itemsPerPage.value,
          currentPage.value * itemsPerPage.value
        );
    });

    const totalPages = computed(() => {
      let total = historyData.value.length;
      if (selectedFilter.value === "recent") total = 20;
      if (selectedFilter.value === "high-sum") {
        total = historyData.value.filter((draw) => draw.sum > 170).length;
      }
      if (selectedFilter.value === "consecutive") {
        total = historyData.value.filter((draw) =>
          draw.features.includes("연속")
        ).length;
      }
      return Math.ceil(total / itemsPerPage.value);
    });

    const getBallClass = (number) => {
      if (number <= 10) return "bg-gradient-to-r from-yellow-400 to-amber-500"; // 1~10: 황금색
      if (number <= 20) return "bg-gradient-to-r from-sky-400 to-blue-500"; // 11~20: 하늘색/파란색
      if (number <= 30) return "bg-gradient-to-r from-pink-400 to-rose-500"; // 21~30: 핑크/장미색
      if (number <= 40) return "bg-gradient-to-r from-gray-500 to-gray-600"; // 31~40: 회색
      return "bg-gradient-to-r from-lime-400 to-green-500"; // 41~45: 연두색
    };

    const getFeatureBadgeClass = (feature) => {
      const classes = {
        연속: "bg-blue-100 text-blue-800",
        고합계: "bg-red-100 text-red-800",
        균형: "bg-green-100 text-green-800",
        홀수: "bg-purple-100 text-purple-800",
        짝수: "bg-orange-100 text-orange-800",
      };
      return classes[feature] || "bg-gray-100 text-gray-800";
    };

    const formatDate = (date) => {
      return new Intl.DateTimeFormat("ko-KR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(new Date(date));
    };

    const analyzeFeatures = (numbers, sum) => {
      const features = [];

      // 연속번호 체크
      for (let i = 0; i < numbers.length - 1; i++) {
        if (numbers[i + 1] - numbers[i] === 1) {
          features.push("연속");
          break;
        }
      }

      // 고합계
      if (sum > 170) features.push("고합계");

      // 홀짝 균형
      const oddCount = numbers.filter((n) => n % 2 === 1).length;
      if (oddCount === 3) features.push("균형");
      else if (oddCount >= 4) features.push("홀수");
      else features.push("짝수");

      return features;
    };

    const refreshHistory = async () => {
      try {
        const rawData = await loadLottoData();
        historyData.value = rawData.map((draw) => ({
          ...draw,
          sum: draw.numbers.reduce((a, b) => a + b, 0),
          features: analyzeFeatures(
            draw.numbers,
            draw.numbers.reduce((a, b) => a + b, 0)
          ),
        }));
      } catch (error) {
        console.error("히스토리 데이터 로드 실패:", error);
      }
    };

    watch(selectedFilter, () => {
      currentPage.value = 1;
    });

    onMounted(() => {
      refreshHistory();
    });

    return {
      historyData,
      selectedFilter,
      currentPage,
      totalDraws,
      avgSum,
      consecutiveRate,
      oddRate,
      filteredHistory,
      totalPages,
      getBallClass,
      getFeatureBadgeClass,
      formatDate,
      refreshHistory,
    };
  },
};
</script>
